import { useCallback, useEffect, useRef, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { getUserSettings, updateUserSetting } from "services";

import { getActiveToken, useUserContext } from "utils";

import { GET_USER_SETTINGS, UPDATE_USER_SETTING } from "consts";

import { UserSettings } from "models";

const DOC_SCAN_PROMO_SETTING = "docScanPromo";

export const useDocScanPromoCard = () => {
  const queryClient = useQueryClient();
  const { user } = useUserContext();
  const token = getActiveToken();

  const [isEligible, setIsEligible] = useState<boolean | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const hasWritten = useRef(false);

  const { data, isFetchedAfterMount, isSuccess } = useQuery<UserSettings>({
    queryKey: [GET_USER_SETTINGS],
    queryFn: ({ signal }) => getUserSettings({ signal }),
    enabled: !!user && !!token,
    // The query cache is persisted to localStorage with a 24h staleTime, so the defaults would
    // decide the gate from device-local data. Only a fetch completed in this mount is authoritative.
    staleTime: 0,
    refetchOnMount: "always",
  });

  const { mutate } = useMutation({
    mutationKey: [UPDATE_USER_SETTING, DOC_SCAN_PROMO_SETTING],
    mutationFn: () => {
      if (!user || !token) return Promise.reject(new Error("Not Authorized"));
      return updateUserSetting({ item: DOC_SCAN_PROMO_SETTING, value: new Date().toISOString() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GET_USER_SETTINGS] });
    },
  });

  useEffect(() => {
    if (isEligible !== null || !isFetchedAfterMount || !isSuccess) return;

    const eligible = !data?.docScanPromo;
    setIsEligible(eligible);

    if (!eligible || hasWritten.current) return;
    // Set before mutating, so a StrictMode double-invoke cannot write twice.
    hasWritten.current = true;
    mutate();
  }, [isEligible, isFetchedAfterMount, isSuccess, data?.docScanPromo, mutate]);

  // No write here — the stored value is a single bit and the first paint above already set it.
  const dismiss = useCallback(() => setIsDismissed(true), []);

  return { isVisible: isEligible === true && !isDismissed, dismiss };
};
